// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

/**
 * Domain validation utilities for company email domain matching.
 * Extracts the logic from orgDomainAliasValidator for use outside Angular forms.
 */
export class DomainValidator {
  /**
   * Extracts the domain part from an email address.
   * @param email - The email address
   * @returns The domain part (lowercase, trimmed) or empty string if invalid
   */
  static extractDomain(email: string | null | undefined): string {
    if (!email) {
      return '';
    }
    return email
      .substring(email.indexOf('@') + 1)
      .toLowerCase()
      .trim();
  }

  /**
   * Validates if an email's domain matches the company's allowed domains.
   *
   * Matching rules:
   * 1. Exact match with primary domains
   * 2. Email domain is a subdomain of a primary domain (e.g., dev.example.com matches example.com)
   * 3. Primary domain is a subdomain of email domain (e.g., example.com matches dev.example.com in domains)
   * 4. Exact match with domain aliases
   *
   * @param email - The email address to validate
   * @param primaryDomains - Comma-separated list of primary domains (e.g., "example.com,test.com")
   * @param domainAliases - Comma-separated list of domain aliases (e.g., "alias1.com,alias2.com")
   * @returns true if the email domain matches, false otherwise
   */
  static isCompanyDomain(
    email: string | null | undefined,
    primaryDomains: string | null | undefined,
    domainAliases?: string | null | undefined
  ): boolean {
    if (!email) {
      return true; // Empty email passes (let email validator handle required)
    }

    const domain = this.extractDomain(email);
    if (!domain) {
      return false;
    }

    // Check primary domains
    const normalizedDomains = this.parseCommaSeparated(primaryDomains);
    const isMainDomainMatch = normalizedDomains.some(mainDomain =>
      this.isMatchingDomain(domain, mainDomain)
    );

    if (isMainDomainMatch) {
      return true;
    }

    // Check domain aliases (exact match only)
    if (!domainAliases) {
      return false;
    }

    const normalizedAliases = this.parseCommaSeparated(domainAliases);
    return normalizedAliases.some(alias => domain === alias);
  }

  /**
   * Checks if a domain matches a target domain (including subdomain relationships).
   */
  private static isMatchingDomain(domain: string, targetDomain: string): boolean {
    // Exact match
    if (domain === targetDomain) {
      return true;
    }

    // Email domain is a subdomain of target (e.g., dev.example.com matches example.com)
    if (domain.endsWith(`.${targetDomain}`)) {
      return true;
    }

    // Target is a subdomain of email domain (e.g., example.com matches dev.example.com)
    // Only match if domain has at least one dot
    if (domain.includes('.') && targetDomain.endsWith(`.${domain}`)) {
      return true;
    }

    return false;
  }

  /**
   * Parses a comma-separated string into an array of lowercase, trimmed values.
   */
  private static parseCommaSeparated(value: string | null | undefined): string[] {
    if (!value) {
      return [];
    }
    return value
      .split(',')
      .map(item => item.toLowerCase().trim())
      .filter(item => item.length > 0);
  }
}
